import React, { useState, useEffect } from "react";
import axios from "axios";
import Chart from "react-apexcharts";

const FinanceAnalytics = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get("https://avastar-pos-backend.onrender.com/api/inventory/products")
            .then(res => {
                setProducts(res.data);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setLoading(false);
            });
    }, []);

    // Analytics Aggregators
    let totalAssetValue = 0;
    let totalStockCount = 0;
    let restockLiability = 0; 
    let totalBatches = 0;

    let categoryValueMap = {};
    let allVariantsFlat = [];

    products.forEach(p => {
        if (p.variants) {
            p.variants.forEach(v => {
                totalBatches += 1;
                const stock = v.currentStock || 0;
                const cost = v.costPerUnit || 0;
                const assetValue = stock * cost;
                
                totalAssetValue += assetValue;
                totalStockCount += stock;
                
                // Restock liability calculation (Cash needed to strictly meet reorder safe margins + 10%)
                if (stock <= v.reorderLevel) {
                    const toBuy = (v.reorderLevel - stock) + (v.reorderLevel * 0.1); 
                    restockLiability += (toBuy * cost);
                }

                // Categorical Mapping
                const catName = p.category?.name || "Uncategorized";
                if (!categoryValueMap[catName]) categoryValueMap[catName] = 0;
                categoryValueMap[catName] += assetValue;

                allVariantsFlat.push({
                    name: `${p.name} - ${v.name}`,
                    category: catName,
                    stock,
                    cost,
                    assetValue
                });
            });
        }
    });

    const categoryStats = Object.entries(categoryValueMap).sort((a,b) => b[1] - a[1]);
    
    // Top 5 locked capital items
    const topAssetHoldings = [...allVariantsFlat].sort((a,b) => b.assetValue - a.assetValue).slice(0, 5);

    // High Cost Risk (Highest single cost per unit)
    const premiumAssets = [...allVariantsFlat].sort((a,b) => b.cost - a.cost).slice(0, 5);

    // ApexCharts Configurations
    const pieOptions = {
        chart: { type: 'pie', background: 'transparent' },
        labels: categoryStats.map(c => c[0]),
        legend: { position: 'bottom', fontSize: '13px' },
        stroke: { width: 0 },
        theme: { palette: 'palette2' },
        tooltip: { y: { formatter: (val) => "Rs " + val.toLocaleString() } }
    };
    const pieSeries = categoryStats.map(c => c[1]);

    const barOptions = {
        chart: { type: 'bar', toolbar: { show: false }, background: 'transparent' },
        plotOptions: { bar: { horizontal: true, borderRadius: 6, distributed: true } },
        dataLabels: { enabled: false },
        xaxis: { categories: topAssetHoldings.map(h => h.name.substring(0, 15) + "...") },
        legend: { show: false },
        theme: { palette: 'palette1' },
        tooltip: { y: { formatter: (val) => "Rs " + val.toLocaleString() } }
    };
    const barSeries = [{ name: "Capital Locked", data: topAssetHoldings.map(h => h.assetValue) }];

    return (
        <div className="page-wrapper bg-light bg-opacity-50">
            <div className="content">
                <div className="page-header d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                    <div>
                        <h4 className="fw-bold mb-1">Advanced Financial Analytics</h4>
                        <p className="text-muted mb-0">Deep dive into capital distribution, risk liabilities, and portfolio density.</p>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
                ) : (
                    <div className="row g-4">
                        {/* KPI Cards */}
                        <div className="col-12 col-md-6 col-lg-3">
                            <div className="card shadow-sm border-0 border-start border-primary border-4 h-100 p-3 bg-white">
                                <h6 className="text-uppercase text-muted fw-bold mb-2" style={{fontSize:'11px', letterSpacing:'1px'}}>Total Asset Valuation</h6>
                                <h3 className="fw-bold text-dark mb-0">Rs {totalAssetValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</h3>
                            </div>
                        </div>
                        <div className="col-12 col-md-6 col-lg-3">
                            <div className="card shadow-sm border-0 border-start border-danger border-4 h-100 p-3 bg-white">
                                <h6 className="text-uppercase text-muted fw-bold mb-2" style={{fontSize:'11px', letterSpacing:'1px'}}>Restock Liability Req.</h6>
                                <h3 className="fw-bold text-dark mb-0">Rs {restockLiability.toLocaleString(undefined, { minimumFractionDigits: 2 })}</h3>
                                <small className="text-muted mt-1" style={{fontSize:'11px'}}>Est. cash needed for safe stock</small>
                            </div>
                        </div>
                        <div className="col-12 col-md-6 col-lg-3">
                            <div className="card shadow-sm border-0 border-start border-success border-4 h-100 p-3 bg-white">
                                <h6 className="text-uppercase text-muted fw-bold mb-2" style={{fontSize:'11px', letterSpacing:'1px'}}>Active Capital Batches</h6>
                                <h3 className="fw-bold text-dark mb-0">{totalBatches.toLocaleString()}</h3>
                            </div>
                        </div>
                        <div className="col-12 col-md-6 col-lg-3">
                            <div className="card shadow-sm border-0 border-start border-info border-4 h-100 p-3 bg-white">
                                <h6 className="text-uppercase text-muted fw-bold mb-2" style={{fontSize:'11px', letterSpacing:'1px'}}>Total Logistics Units</h6>
                                <h3 className="fw-bold text-dark mb-0">{totalStockCount.toLocaleString()}</h3>
                            </div>
                        </div>
                        
                        {/* Charts Row */}
                        <div className="col-12 col-xl-6">
                            <div className="card shadow-sm border-0 bg-white h-100">
                                <div className="card-header bg-white border-0 pt-4 pb-0">
                                    <h5 className="fw-bold text-dark mb-0">Asset Distribution by Category</h5>
                                    <p className="text-muted small">Visualized split of where your capital resides.</p>
                                </div>
                                <div className="card-body d-flex justify-content-center align-items-center">
                                    {pieSeries.length > 0 ? (
                                        <Chart options={pieOptions} series={pieSeries} type="pie" height={350} width="100%" />
                                    ) : (
                                        <p className="text-muted">No data available</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-xl-6">
                            <div className="card shadow-sm border-0 bg-white h-100">
                                <div className="card-header bg-white border-0 pt-4 pb-0">
                                    <h5 className="fw-bold text-dark mb-0">Top 5 Capital Sinkholes</h5>
                                    <p className="text-muted small">Highest gross asset value locked in specific batches.</p>
                                </div>
                                <div className="card-body">
                                    {barSeries[0].data.length > 0 ? (
                                        <Chart options={barOptions} series={barSeries} type="bar" height={330} width="100%" />
                                    ) : (
                                        <p className="text-muted">No data available</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Data Tables */}
                        <div className="col-12">
                            <div className="card shadow-sm border-0 bg-white">
                                <div className="card-header bg-white pt-4 pb-3">
                                    <h5 className="fw-bold text-dark mb-0">Premium Asset Classes</h5>
                                    <p className="text-muted small mb-0">Individual batches with the most expensive singular unit buying cost.</p>
                                </div>
                                <div className="card-body p-0">
                                    <div className="table-responsive">
                                        <table className="table table-hover mb-0 align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th className="border-0 ps-4">Asset Identifer</th>
                                                    <th className="border-0">Category</th>
                                                    <th className="border-0 text-center">Unit Cost (Buying)</th>
                                                    <th className="border-0 text-center">Current Vault (Stock)</th>
                                                    <th className="border-0 text-end pe-4">Total Assets Retained</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {premiumAssets.length > 0 ? premiumAssets.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td className="ps-4 fw-bold">{item.name}</td>
                                                        <td><span className="badge bg-secondary bg-opacity-10 text-secondary">{item.category}</span></td>
                                                        <td className="text-center text-danger fw-bold">Rs {item.cost.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                                        <td className="text-center fw-medium">{item.stock}</td>
                                                        <td className="text-end pe-4 fw-bold">Rs {item.assetValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                                    </tr>
                                                )) : (
                                                    <tr><td colSpan="5" className="text-center py-4 text-muted">No asset classes to display.</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                )}
            </div>
        </div>
    );
};
export default FinanceAnalytics;
